import { useTranslation } from 'react-i18next';
import type { GroupScreenPreset } from './buildGroupCardModel';

type GroupsEmptyStateProps = {
  starters: GroupScreenPreset[];
  onNew: () => void;
  onStarter: (preset: GroupScreenPreset) => void;
  busy?: boolean;
};

/**
 * Empty Groups onboarding — split action + example preview
 * (design screenshot: “Change lots of screens at once”).
 */
export function GroupsEmptyState({
  starters,
  onNew,
  onStarter,
  busy = false,
}: GroupsEmptyStateProps) {
  const { t } = useTranslation('groups');
  return (
    <section
      className="grid w-full overflow-hidden rounded-2xl border md:grid-cols-2"
      style={{
        background: 'var(--gs-panel)',
        borderColor: 'var(--gs-line)',
        animation: 'gs-in 260ms ease both',
      }}
    >
      <div className="flex flex-col gap-7 px-[clamp(24px,4vw,40px)] py-[clamp(28px,4vw,40px)]">
        <div className="flex flex-col gap-2.5">
          <h2
            className="m-0 text-[26px] font-extrabold tracking-[-0.03em]"
            style={{ color: 'var(--gs-ink)', lineHeight: 1.15 }}
          >
            {t('empty.title')}
          </h2>
          <p
            className="m-0 max-w-[34ch] text-[15px]"
            style={{ color: 'var(--gs-ink2)', lineHeight: 1.5, textWrap: 'pretty' }}
          >
            {t('empty.description')}
          </p>
        </div>

        {starters.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
              {t('empty.startWithOneClick')}
            </span>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {starters.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  disabled={busy}
                  onClick={() => onStarter(preset)}
                  className="inline-flex items-center gap-2 rounded-full border px-3.5 py-[11px] text-start text-[13.5px] font-semibold disabled:opacity-50"
                  style={{
                    color: 'var(--gs-ink)',
                    background: 'transparent',
                    borderColor: 'var(--gs-line)',
                    fontFamily: 'inherit',
                  }}
                >
                  <span
                    className="text-base font-medium leading-none"
                    style={{ color: 'var(--gs-muted)' }}
                    aria-hidden
                  >
                    +
                  </span>
                  <span className="min-w-0 truncate">{preset.label}</span>
                  <span
                    className="ms-auto shrink-0 font-medium"
                    style={{ color: 'var(--gs-muted)' }}
                  >
                    {t('card.screens', { count: preset.screenIds.length })}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
          <button
            type="button"
            disabled={busy}
            onClick={onNew}
            className="rounded-[10px] px-[18px] py-3 text-sm font-bold disabled:opacity-50"
            style={{
              color: 'var(--gs-accent-ink)',
              background: 'var(--gs-accent-solid)',
              fontFamily: 'inherit',
            }}
          >
            {t('empty.pickScreens')}
          </button>
          <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
            {t('empty.takesAbout', { value: 30 })}
          </span>
        </div>
      </div>

      <aside
        className="flex flex-col justify-center gap-4 border-t px-[clamp(24px,4vw,40px)] py-[clamp(28px,4vw,40px)] md:border-s md:border-t-0"
        style={{ borderColor: 'var(--gs-line)' }}
        aria-hidden
      >
        <span
          className="text-[11px] font-bold uppercase tracking-[0.14em]"
          style={{ color: 'var(--gs-muted)' }}
        >
          {t('empty.example')}
        </span>

        <div
          className="flex flex-col gap-3.5 rounded-[14px] border px-5 py-[18px]"
          style={{
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            boxShadow: '0 10px 28px rgba(10, 10, 11, 0.06)',
          }}
        >
          <div className="flex flex-col gap-1">
            <span
              className="text-[17px] font-extrabold tracking-[-0.02em]"
              style={{ color: 'var(--gs-ink)' }}
            >
              {t('empty.exampleGroup')}
            </span>
            <span
              className="flex flex-wrap items-center gap-1.5 text-[13px]"
              style={{ color: 'var(--gs-muted)' }}
            >
              <span>{t('card.screens', { count: 3 })}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <span
                  className="h-[7px] w-[7px] rounded-full"
                  style={{ background: 'var(--gs-ok)' }}
                />
                <span style={{ color: 'var(--gs-ink2)' }}>
                  {t('card.healthOnline', { count: 3 })}
                </span>
              </span>
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {[
              t('empty.exampleFrontDoor'),
              t('empty.exampleReception'),
              t('empty.exampleWaitingArea'),
            ].map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-[7px] rounded-full border px-[11px] py-1.5 text-[13px] font-semibold"
                style={{ color: 'var(--gs-ink2)', borderColor: 'var(--gs-line)' }}
              >
                <span
                  className="h-[7px] w-[7px] rounded-full"
                  style={{ background: 'var(--gs-ok)' }}
                />
                {name}
              </span>
            ))}
          </div>

          <div
            className="flex items-center justify-between gap-3 rounded-[10px] border px-3.5 py-2.5"
            style={{
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
            }}
          >
            <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
              {t('empty.showingOnAll')}
            </span>
            <span className="text-[13.5px] font-bold" style={{ color: 'var(--gs-ink)' }}>
              {t('empty.examplePlaylist')}
            </span>
          </div>
        </div>

        <p
          className="m-0 text-center text-[13px]"
          style={{ color: 'var(--gs-muted)', lineHeight: 1.4 }}
        >
          {t('empty.exampleFootnote')}
        </p>
      </aside>
    </section>
  );
}
